import React, { useEffect, useState } from "react";
import {
  FaBuilding,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaUniversity,
  FaFileInvoice,
  FaPenNib,
  FaSave,
  FaUndo,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUpload,
  FaTrashAlt,
} from "react-icons/fa";
import { IoSettingsOutline } from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiendpoints from "../../apiendpoints.json";
import { getAuthHeaders, hasPermission } from "../../utils/auth";

const BASE_URL = apiendpoints.equityUrl || "http://localhost:5217";
const CURRENT_ENTITY_CODE = (process.env.REACT_APP_ENTITY_NAME || "equity").trim().toLowerCase();
const CURRENT_ENTITY_ID = Number(process.env.REACT_APP_ENTITY_ID || 2);

const INDIAN_STATES = [
  { name: "Andhra Pradesh", code: "37" },
  { name: "Arunachal Pradesh", code: "12" },
  { name: "Assam", code: "18" },
  { name: "Bihar", code: "10" },
  { name: "Chandigarh", code: "04" },
  { name: "Chhattisgarh", code: "22" },
  { name: "Delhi", code: "07" },
  { name: "Goa", code: "30" },
  { name: "Gujarat", code: "24" },
  { name: "Haryana", code: "06" },
  { name: "Himachal Pradesh", code: "02" },
  { name: "Jammu & Kashmir", code: "01" },
  { name: "Jharkhand", code: "20" },
  { name: "Karnataka", code: "29" },
  { name: "Kerala", code: "32" },
  { name: "Ladakh", code: "38" },
  { name: "Madhya Pradesh", code: "23" },
  { name: "Maharashtra", code: "27" },
  { name: "Manipur", code: "14" },
  { name: "Meghalaya", code: "17" },
  { name: "Mizoram", code: "15" },
  { name: "Nagaland", code: "13" },
  { name: "Odisha", code: "21" },
  { name: "Puducherry", code: "34" },
  { name: "Punjab", code: "03" },
  { name: "Rajasthan", code: "08" },
  { name: "Sikkim", code: "11" },
  { name: "Tamil Nadu", code: "33" },
  { name: "Telangana", code: "36" },
  { name: "Tripura", code: "16" },
  { name: "Uttar Pradesh", code: "09" },
  { name: "Uttarakhand", code: "05" },
  { name: "West Bengal", code: "19" },
];

const initialForm = {
  entityId: CURRENT_ENTITY_ID,
  entityCode: CURRENT_ENTITY_CODE,
  companyName: "",
  officeAddress: "",
  gstin: "",
  stateName: "Madhya Pradesh",
  stateCode: "23",
  pan: "",
  cin: "",
  sebiRegistrationNo: "",
  contactPhone: "",
  email: "",
  bankAccountHolderName: "",
  bankName: "",
  bankAccountNo: "",
  bankBranchAndIfsc: "",
  bankSwiftCode: "",
  upiId: "",
  invoicePrefix: "INV",
  declaration:
    "We declare that this invoice shows the actual price of the services described and that all particulars are true and correct.",
  termsAndConditions:
    "1. Payment is due within 7 days of invoice date.\n2. All investments and advisory are subject to market risks.\n3. Disputed invoices must be communicated in writing within 5 business days.",
  authorizedSignatoryTitle: "Authorized Signatory",
  signatureImageUrl: "",
  isActive: true,
};

const EntitySettings = () => {
  const [form, setForm] = useState(initialForm);
  const [initialData, setInitialData] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const canUpdate = hasPermission("settings", "update");

  const loadEntityProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/entity-profile`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to load entity profiles.");
      }

      const profiles = Array.isArray(result.payload)
        ? result.payload
        : result.payload?.profiles || [];

      const matchingProfiles = profiles.filter(
        (profile) =>
          Number(profile.entityId) === CURRENT_ENTITY_ID &&
          profile.entityCode?.trim().toLowerCase() === CURRENT_ENTITY_CODE
      );
      setAvailableProfiles(matchingProfiles);

      const current = matchingProfiles[0];

      if (current) {
        const loadedForm = {
          entityId: CURRENT_ENTITY_ID,
          entityCode: current.entityCode ?? CURRENT_ENTITY_CODE,
          companyName: current.companyName ?? "",
          officeAddress: current.officeAddress ?? "",
          gstin: current.gstin ?? "",
          stateName: current.stateName ?? "Madhya Pradesh",
          stateCode: current.stateCode ?? "23",
          pan: current.pan ?? "",
          cin: current.cin ?? "",
          sebiRegistrationNo: current.sebiRegistrationNo ?? "",
          contactPhone: current.contactPhone ?? "",
          email: current.email ?? "",
          bankAccountHolderName: current.bankAccountHolderName ?? "",
          bankName: current.bankName ?? "",
          bankAccountNo: current.bankAccountNo ?? "",
          bankBranchAndIfsc: current.bankBranchAndIfsc ?? "",
          bankSwiftCode: current.bankSwiftCode ?? "",
          upiId: current.upiId ?? "",
          invoicePrefix: current.invoicePrefix ?? "INV",
          declaration: current.declaration ?? initialForm.declaration,
          termsAndConditions:
            current.termsAndConditions ?? initialForm.termsAndConditions,
          authorizedSignatoryTitle:
            current.authorizedSignatoryTitle ?? "Authorized Signatory",
          signatureImageUrl: current.signatureImageUrl ?? "",
          isActive: current.isActive ?? true,
        };
        setForm(loadedForm);
        setInitialData(loadedForm);
        setProfileId(current.id);
      } else {
        setForm(initialForm);
        setInitialData(initialForm);
        setProfileId(null);
      }
    } catch (err) {
      console.error("Error loading entity profile:", err);
      toast.error(err.message || "Could not retrieve entity profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntityProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStateChange = (e) => {
    const selectedStateName = e.target.value;
    const matchedState = INDIAN_STATES.find((s) => s.name === selectedStateName);
    setForm((prev) => ({
      ...prev,
      stateName: selectedStateName,
      stateCode: matchedState ? matchedState.code : prev.stateCode,
    }));
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG, WebP).");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Signature image file size must be under 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setForm((prev) => ({
        ...prev,
        signatureImageUrl: dataUrl,
      }));
      toast.success("Signature uploaded successfully.");
    };
    reader.onerror = () => {
      toast.error("Failed to read the signature file.");
    };
    reader.readAsDataURL(file);
  };

  const handleClearSignature = () => {
    setForm((prev) => ({
      ...prev,
      signatureImageUrl: "",
    }));
    toast.info("Signature cleared.");
  };

  const handleReset = () => {
    if (initialData) {
      setForm(initialData);
      toast.info("Changes reset to last saved data.");
    } else {
      setForm(initialForm);
    }
  };

  const handleSwitchProfile = (p) => {
    const loadedForm = {
      entityId: CURRENT_ENTITY_ID,
      entityCode: CURRENT_ENTITY_CODE,
      companyName: p.companyName ?? "",
      officeAddress: p.officeAddress ?? "",
      gstin: p.gstin ?? "",
      stateName: p.stateName ?? "Madhya Pradesh",
      stateCode: p.stateCode ?? "23",
      pan: p.pan ?? "",
      cin: p.cin ?? "",
      sebiRegistrationNo: p.sebiRegistrationNo ?? "",
      contactPhone: p.contactPhone ?? "",
      email: p.email ?? "",
      bankAccountHolderName: p.bankAccountHolderName ?? "",
      bankName: p.bankName ?? "",
      bankAccountNo: p.bankAccountNo ?? "",
      bankBranchAndIfsc: p.bankBranchAndIfsc ?? "",
      bankSwiftCode: p.bankSwiftCode ?? "",
      upiId: p.upiId ?? "",
      invoicePrefix: p.invoicePrefix ?? "INV",
      declaration: p.declaration ?? initialForm.declaration,
      termsAndConditions: p.termsAndConditions ?? initialForm.termsAndConditions,
      authorizedSignatoryTitle:
        p.authorizedSignatoryTitle ?? "Authorized Signatory",
      signatureImageUrl: p.signatureImageUrl ?? "",
      isActive: p.isActive ?? true,
    };
    setForm(loadedForm);
    setInitialData(loadedForm);
    setProfileId(p.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canUpdate) {
      toast.error("You do not have permission to modify entity settings.");
      return;
    }

    // Required fields validation
    if (!form.companyName?.trim()) {
      toast.error("Company Name is required.");
      return;
    }
    if (!form.officeAddress?.trim()) {
      toast.error("Office Address is required.");
      return;
    }
    if (!form.gstin?.trim()) {
      toast.error("GSTIN is required.");
      return;
    }
    if (!form.pan?.trim()) {
      toast.error("PAN is required.");
      return;
    }
    if (!form.stateName?.trim()) {
      toast.error("State Name is required.");
      return;
    }
    if (!form.stateCode?.trim()) {
      toast.error("State Code is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entityId: CURRENT_ENTITY_ID,
        entityCode: CURRENT_ENTITY_CODE,
        companyName: form.companyName.trim(),
        officeAddress: form.officeAddress.trim(),
        gstin: form.gstin.trim().toUpperCase(),
        stateName: form.stateName.trim(),
        stateCode: form.stateCode.trim(),
        pan: form.pan.trim().toUpperCase(),
        cin: form.cin?.trim() || null,
        sebiRegistrationNo: form.sebiRegistrationNo?.trim() || null,
        contactPhone: form.contactPhone?.trim() || null,
        email: form.email?.trim() || null,
        bankAccountHolderName: form.bankAccountHolderName?.trim() || null,
        bankName: form.bankName?.trim() || null,
        bankAccountNo: form.bankAccountNo?.trim() || null,
        bankBranchAndIfsc: form.bankBranchAndIfsc?.trim() || null,
        bankSwiftCode: form.bankSwiftCode?.trim() || null,
        upiId: form.upiId?.trim() || null,
        invoicePrefix: (form.invoicePrefix?.trim() || "INV").toUpperCase(),
        declaration: form.declaration?.trim() || null,
        termsAndConditions: form.termsAndConditions?.trim() || null,
        authorizedSignatoryTitle:
          form.authorizedSignatoryTitle?.trim() || "Authorized Signatory",
        signatureImageUrl: form.signatureImageUrl?.trim() || null,
        isActive: Boolean(form.isActive),
      };

      const url = profileId
        ? `${BASE_URL}/api/entity-profile/${profileId}`
        : `${BASE_URL}/api/entity-profile`;

      const method = profileId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save entity profile.");
      }

      toast.success(
        profileId
          ? "Entity profile updated successfully!"
          : "Entity profile created successfully!"
      );

      if (data.payload?.id) {
        setProfileId(data.payload.id);
      }
      setInitialData({ ...form });

      // Refresh profiles list in background
      await loadEntityProfile();
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.message || "Failed to save entity profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mx-auto max-w-6xl space-y-6">
        {/* Top Header Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <IoSettingsOutline size={26} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-800">
                    Entity Settings
                  </h1>
                  {form.isActive ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                      <FaCheckCircle className="text-xs" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                      <FaTimesCircle className="text-xs" /> Inactive
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-slate-500">
                  Manage company information, registered tax identifiers, bank accounts, and invoice branding.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={loadEntityProfile}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
              >
                <FaSyncAlt className={loading ? "animate-spin text-xs" : "text-xs"} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={saving || loading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
              >
                <FaUndo className="text-xs" />
                Reset
              </button>
              {canUpdate ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? "Saving Changes..." : "Save Profile"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-700">
                  Read Only
                </span>
              )}
            </div>
          </div>

          {/* Multiple Profiles Switcher if present */}
          {availableProfiles.length > 1 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Available Entity Profiles in DB:
              </span>
              <div className="mt-2 flex flex-wrap gap-2">
                {availableProfiles.map((p) => {
                  const isCurrent = profileId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleSwitchProfile(p)}
                      className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                        isCurrent
                          ? "bg-blue-600 text-white shadow"
                          : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <FaBuilding className="text-xs" />
                      <span>{p.companyName || `Entity #${p.entityId}`}</span>
                      <span
                        className={`rounded px-1.5 py-0.2 text-[10px] uppercase font-bold ${
                          isCurrent
                            ? "bg-blue-700 text-white"
                            : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {p.entityCode || `ID:${p.entityId}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Section Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-white px-4 rounded-xl shadow-sm overflow-x-auto">
          {[
            { id: "company", label: "Company & Legal", icon: FaBuilding },
            { id: "address", label: "Address & Contact", icon: FaMapMarkerAlt },
            { id: "banking", label: "Banking & Settlement", icon: FaUniversity },
            { id: "invoice", label: "Invoicing & Branding", icon: FaFileInvoice },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? "border-blue-600 text-blue-600 font-semibold"
                    : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800"
                }`}
              >
                <Icon className={active ? "text-blue-600" : "text-slate-400"} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Loading state indicator */}
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-8">
            <div className="flex flex-col items-center gap-3">
              <FaSyncAlt className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm font-medium text-slate-500">
                Loading entity profile details...
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 1. COMPANY & LEGAL IDENTIFIERS */}
            {(activeTab === "company" || activeTab === "all") && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
                  <FaBuilding className="text-lg text-blue-600" />
                  <h2 className="text-lg font-bold text-slate-800">
                    Company & Statutory Profile
                  </h2>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Company / Registered Legal Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={form.companyName}
                      onChange={handleChange}
                      placeholder="e.g. Edumitram Financial Services Pvt Ltd"
                      required
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
              {/*
                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Entity Code <span className="text-slate-400 font-normal">(System Key)</span>
                    </label>
                    <input
                      type="text"
                      name="entityCode"
                      value={form.entityCode}
                      onChange={handleChange}
                      placeholder="e.g. equity"
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  */}

                 {/* <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Entity ID <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="entityId"
                      value={form.entityId}
                      onChange={handleChange}
                      min="1"
                      required
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>*/}

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      GSTIN <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="gstin"
                      value={form.gstin}
                      onChange={handleChange}
                      placeholder="23AAAAA0000A1Z5"
                      maxLength={20}
                      required
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      PAN <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="pan"
                      value={form.pan}
                      onChange={handleChange}
                      placeholder="AAAAA0000A"
                      maxLength={20}
                      required
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
{/* 
                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      CIN (Corporate Identity Number)
                    </label>
                    <input
                      type="text"
                      name="cin"
                      value={form.cin}
                      onChange={handleChange}
                      placeholder="U12345MP2020PTC012345"
                      maxLength={50}
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div> */}

                  {/* <div>
                    <label className="text-xs font-semibold text-slate-700">
                      SEBI Registration No.
                    </label>
                    <input
                      type="text"
                      name="sebiRegistrationNo"
                      value={form.sebiRegistrationNo}
                      onChange={handleChange}
                      placeholder="INH000000000"
                      maxLength={100}
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div> */}

                  {/* <div className="flex items-center gap-3 pt-6">
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={form.isActive}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                    </label>
                    <span className="text-sm font-medium text-slate-700">
                      Active Entity Profile
                    </span>
                  </div> */}
                </div>
              </div>
            )}

            {/* 2. ADDRESS & CONTACT DETAILS */}
            {(activeTab === "address" || activeTab === "all") && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
                  <FaMapMarkerAlt className="text-lg text-blue-600" />
                  <h2 className="text-lg font-bold text-slate-800">
                    Registered Address & Contact
                  </h2>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="text-xs font-semibold text-slate-700">
                      Registered Office Address <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      name="officeAddress"
                      value={form.officeAddress}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Complete physical office address for invoices and tax declarations"
                      required
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      State Name <span className="text-rose-500">*</span>
                    </label>
                    <select
                      name="stateName"
                      value={form.stateName}
                      onChange={handleStateChange}
                      required
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s.code} value={s.name}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      GST State Code <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="stateCode"
                      value={form.stateCode}
                      onChange={handleChange}
                      placeholder="e.g. 23"
                      maxLength={10}
                      required
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Official Contact Phone
                    </label>
                    <div className="relative mt-1">
                      <FaPhoneAlt className="absolute left-3.5 top-3 text-xs text-slate-400" />
                      <input
                        type="text"
                        name="contactPhone"
                        value={form.contactPhone}
                        onChange={handleChange}
                        placeholder="+91 9876543210"
                        maxLength={30}
                        className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Official Billing / Support Email
                    </label>
                    <div className="relative mt-1">
                      <FaEnvelope className="absolute left-3.5 top-3 text-xs text-slate-400" />
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="compliance@edumitram.com"
                        maxLength={150}
                        className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3. BANKING & SETTLEMENT DETAILS */}
            {(activeTab === "banking" || activeTab === "all") && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
                  <FaUniversity className="text-lg text-blue-600" />
                  <h2 className="text-lg font-bold text-slate-800">
                    Default Banking & Settlement Details
                  </h2>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={form.bankName}
                      onChange={handleChange}
                      placeholder="e.g. HDFC Bank"
                      maxLength={150}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Bank Account Holder Name
                    </label>
                    <input
                      type="text"
                      name="bankAccountHolderName"
                      value={form.bankAccountHolderName}
                      onChange={handleChange}
                      placeholder="e.g. Edumitram Financial Services"
                      maxLength={200}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccountNo"
                      value={form.bankAccountNo}
                      onChange={handleChange}
                      placeholder="e.g. 50200012345678"
                      maxLength={50}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Branch & IFSC Code
                    </label>
                    <input
                      type="text"
                      name="bankBranchAndIfsc"
                      value={form.bankBranchAndIfsc}
                      onChange={handleChange}
                      placeholder="e.g. Indore Main Branch, HDFC0001234"
                      maxLength={200}
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      SWIFT / BIC Code
                    </label>
                    <input
                      type="text"
                      name="bankSwiftCode"
                      value={form.bankSwiftCode}
                      onChange={handleChange}
                      placeholder="e.g. HDFCINBB"
                      maxLength={50}
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      UPI ID / VPA
                    </label>
                    <input
                      type="text"
                      name="upiId"
                      value={form.upiId}
                      onChange={handleChange}
                      placeholder="e.g. edumitram@hdfcbank"
                      maxLength={100}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 4. INVOICE BRANDING & LEGAL DISCLAIMERS */}
            {(activeTab === "invoice" || activeTab === "all") && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
                  <FaFileInvoice className="text-lg text-blue-600" />
                  <h2 className="text-lg font-bold text-slate-800">
                    Invoice Branding & Legal Disclaimers
                  </h2>
                </div>

                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  {/* <div> 
                    <label className="text-xs font-semibold text-slate-700">
                      Invoice Number Prefix
                    </label>
                    <input
                      type="text"
                      name="invoicePrefix"
                      value={form.invoicePrefix}
                      onChange={handleChange}
                      placeholder="e.g. EQ, TF, or INV"
                      maxLength={20}
                      className="mt-1 h-10 w-full uppercase rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-mono font-bold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      Appears at the start of generated tax invoices (e.g. {form.invoicePrefix || "INV"}/2026-27/001).
                    </p>
                  </div>
                  */}

                  <div>
                    <label className="text-xs font-semibold text-slate-700">
                      Authorized Signatory Title
                    </label>
                    <input
                      type="text"
                      name="authorizedSignatoryTitle"
                      value={form.authorizedSignatoryTitle}
                      onChange={handleChange}
                      placeholder="e.g. Authorized Signatory / Director"
                      maxLength={150}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Signature Upload & Preview */}
                  {/* <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                    <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <FaPenNib className="text-blue-600" /> Digital Signature Image
                      </span>
                      {form.signatureImageUrl && (
                        <button
                          type="button"
                          onClick={handleClearSignature}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700"
                        >
                          <FaTrashAlt className="text-[11px]" /> Remove Signature
                        </button>
                      )}
                    </label>

                    <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center">
                      {form.signatureImageUrl ? (
                        <div className="relative flex h-24 w-48 items-center justify-center rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
                          <img
                            src={form.signatureImageUrl}
                            alt="Signature Preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex h-24 w-48 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-xs text-slate-400">
                          No signature attached
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-white border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                          <FaUpload className="text-blue-600" />
                          <span>Upload Signature Image</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleSignatureUpload}
                            className="hidden"
                          />
                        </label>
                        <p className="text-[11px] text-slate-400">
                          Recommended format: PNG transparent background, max 2MB.
                        </p>
                      </div>
                    </div>
                  </div> */}

                  {/* Declaration */}
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Standard Invoice Declaration Text
                    </label>
                    <textarea
                      name="declaration"
                      value={form.declaration}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Declaration printed at bottom of tax invoices..."
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Terms and Conditions */}
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Invoice Terms and Conditions
                    </label>
                    <textarea
                      name="termsAndConditions"
                      value={form.termsAndConditions}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Terms and conditions printed on tax invoices..."
                      className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Save Bar */}
            {/* <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
           <span className="text-xs text-slate-500">
                {profileId
                  ? `Editing Entity Profile #${profileId} (${form.entityCode || "ID:" + form.entityId})`
                  : "Creating new Entity Profile"}
              </span> 
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving || loading}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Reset
                </button>
                {canUpdate && (
                  <button
                    type="submit"
                    disabled={saving || loading}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                  >
                    <FaSave />
                    {saving ? "Saving Changes..." : "Save Profile"}
                  </button>
                )}
              </div>
            </div> */}
          </form>
        )}
      </div>
    </div>
  );
};

export default EntitySettings;
